import { ResponsivePie } from '@nivo/pie';
import { ChartFrame, type ChartCardProps } from '~/components/dashboard/charts/ChartCard';
import { ChartLegend } from '~/components/dashboard/charts/ChartLegend';
import { useChartTheme } from '~/components/dashboard/charts/useChartTheme';
import type { TokenName } from '~/lib/theme-colors';
import { formatNumber } from '~/lib/format';

export interface DonutSlice {
  id: string;
  label: string;
  value: number;
  /** Hover text on the legend row. */
  description?: string;
  /** A slice's colour is its state or category (DESIGN.md §8.1). */
  token: TokenName;
}

export interface DonutCardProps extends ChartCardProps {
  slices: DonutSlice[];
  /** What the slices count: `{ one: 'error', other: 'errors' }`. Shown under the total and after each percentage. */
  unit: { one: string; other: string };
}

const noun = (count: number, unit: DonutCardProps['unit']) => (count === 1 ? unit.one : unit.other);

/** Proportions as a donut: the total in the centre, an HTML legend beside it with `NN% • N items` (DESIGN.md §8.1). */
export function DonutCard({ slices, unit, ...card }: DonutCardProps) {
  const { nivoTheme, color } = useChartTheme();
  const colored = slices.map((slice) => ({ ...slice, color: color(slice.token) }));
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <ChartFrame
      {...card}
      isEmpty={total === 0}
      boxClass="size-64 shrink-0"
      layout="flex flex-col items-center gap-6 sm:flex-row sm:justify-center"
      legend={
        <ChartLegend
          className="space-y-2"
          items={colored.map((slice) => ({
            id: slice.id,
            label: slice.label,
            color: slice.color,
            title: slice.description,
            detail: `${Math.round((slice.value / total) * 100)}% • ${formatNumber(slice.value)} ${noun(slice.value, unit)}`,
          }))}
        />
      }
    >
      <ResponsivePie
        data={colored}
        theme={nivoTheme}
        margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
        innerRadius={0.65}
        padAngle={0.7}
        cornerRadius={3}
        activeOuterRadiusOffset={8}
        colors={{ datum: 'data.color' }}
        borderWidth={0}
        enableArcLinkLabels={false}
        enableArcLabels={false}
      />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold text-gray-800 dark:text-white/90">
          {formatNumber(total)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{noun(total, unit)}</span>
      </div>
    </ChartFrame>
  );
}
